/**
 * SnowSessionSection – sekcja "Prace Dodatkowe / Odśnieżanie" w widoku szczegółów nieruchomości.
 * Start/Stop sesji, licznik czasu, opcjonalne zdjęcia przy zakończeniu.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Play, Square, Snowflake, Loader2, Camera, ImagePlus, Check, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { compressImage } from '@/lib/image-compression';
import { closeWorkSession, fetchOpenWorkSessions, startWorkSession } from '@/lib/workSessions';

const BUCKET_NAME = 'photos';
const SNOW_SESSION_TYPE = 'snow_removal';
const GPS_MAX_DISTANCE_METERS = 200;

interface SnowSession {
  id: string;
  started_at: string;
}

interface SnowSessionSectionProps {
  locationId: string;
  orgId: string | null;
  latitude?: number | null;
  longitude?: number | null;
  onSessionChange?: () => void;
  disabled?: boolean;
}

function formatElapsedMinutes(startedAt: string): number {
  const start = new Date(startedAt).getTime();
  const now = Date.now();
  return Math.floor((now - start) / 60_000);
}

/** Haversine formula – distance in meters between two coordinates */
function haversineDistanceMeters(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
): number {
  const R = 6_371_000; // Earth radius in meters
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

export function SnowSessionSection({
  locationId,
  orgId,
  latitude,
  longitude,
  onSessionChange,
  disabled = false,
}: SnowSessionSectionProps) {
  const [session, setSession] = useState<SnowSession | null>(null);
  const [elapsedMinutes, setElapsedMinutes] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isStarting, setIsStarting] = useState(false);
  const [isStopping, setIsStopping] = useState(false);
  const [showStartConfirmDialog, setShowStartConfirmDialog] = useState(false);
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [pendingStopTaskId, setPendingStopTaskId] = useState<string | null>(null);
  const [photoFiles, setPhotoFiles] = useState<File[]>([]);
  const [previewUrls, setPreviewUrls] = useState<string[]>([]);
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchActiveSession = useCallback(async () => {
    if (!locationId || !orgId) {
      setSession(null);
      return;
    }
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setSession(null);
        return;
      }
      const { data, error } = await supabase
        .from('cleaning_tasks')
        .select('id, started_at, created_at')
        .eq('location_id', locationId)
        .eq('org_id', orgId)
        .eq('assigned_staff_id', user.id)
        .eq('status', 'in_progress')
        .contains('check_in_metadata', { session_type: SNOW_SESSION_TYPE })
        .maybeSingle();

      if (error) {
        console.error('[SnowSessionSection] Błąd pobierania sesji odśnieżania:', error);
        setSession(null);
        return;
      }
      if (data) {
        setSession({ id: data.id, started_at: data.started_at ?? data.created_at ?? new Date().toISOString() });
        setElapsedMinutes(formatElapsedMinutes(data.started_at ?? data.created_at ?? new Date().toISOString()));
      } else {
        setSession(null);
        setElapsedMinutes(0);
      }
    } catch (err) {
      console.error('[SnowSessionSection] Błąd fetchActiveSession:', err);
      setSession(null);
    }
  }, [locationId, orgId]);

  useEffect(() => {
    if (!locationId || !orgId) return;
    fetchActiveSession();
  }, [locationId, orgId, fetchActiveSession]);

  // Timer: co 10 s aktualizuj elapsed
  useEffect(() => {
    if (!session?.started_at) {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      return;
    }
    const tick = () => setElapsedMinutes(formatElapsedMinutes(session.started_at));
    tick();
    timerRef.current = setInterval(tick, 10_000);
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [session?.started_at]);

  // Revoke object URLs to avoid memory leaks
  const clearPreviews = useCallback(() => {
    setPreviewUrls((prev) => {
      prev.forEach((url) => URL.revokeObjectURL(url));
      return [];
    });
  }, []);

  const handleStartConfirm = () => {
    if (!orgId || !locationId || isStarting || disabled) return;
    setShowStartConfirmDialog(false);
    toast.info('Rozpoczynanie...');
    setIsLoading(true);
    setIsStarting(true);
    setTimeout(() => {
      runStartWithGps();
    }, 100);
  };

  const runStartWithGps = async () => {
    if (!orgId || !locationId) return;
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error('Nie jesteś zalogowany.');
        return;
      }

      if (
        latitude != null &&
        longitude != null &&
        typeof latitude === 'number' &&
        typeof longitude === 'number' &&
        !Number.isNaN(latitude) &&
        !Number.isNaN(longitude)
      ) {
        try {
          const position = await new Promise<GeolocationPosition>((resolve, reject) => {
            if (!navigator?.geolocation) {
              reject(new Error('Geolocation not supported'));
              return;
            }
            navigator.geolocation.getCurrentPosition(resolve, reject, {
              enableHighAccuracy: true,
              timeout: 15_000,
              maximumAge: 0,
            });
          });
          const userLat = position.coords.latitude;
          const userLon = position.coords.longitude;
          const distanceM = haversineDistanceMeters(latitude, longitude, userLat, userLon);
          if (distanceM > GPS_MAX_DISTANCE_METERS) {
            toast.error(
              `Jesteś zbyt daleko od obiektu, aby rozpocząć pracę (${Math.round(distanceM)} m).`
            );
            return;
          }
        } catch (gpsErr) {
          console.warn('[SnowSessionSection] GPS niedostępny, kontynuuję bez weryfikacji:', gpsErr);
          toast.warning('Nie udało się pobrać lokalizacji, ale rozpoczynam pracę.');
        }
      }

      const now = new Date().toISOString();
      const { data, error } = await supabase
        .from('cleaning_tasks')
        .insert({
          org_id: orgId,
          location_id: locationId,
          assigned_staff_id: user.id,
          status: 'in_progress',
          task_type: 'employee_extra',
          scheduled_at: now,
          started_at: now,
          section_id: null,
          check_in_metadata: { session_type: SNOW_SESSION_TYPE },
          staff_notes: 'Odśnieżanie',
        })
        .select('id, started_at')
        .single();

      if (error) {
        console.error('[SnowSessionSection] Błąd tworzenia sesji:', error);
        toast.error('Nie udało się rozpocząć odśnieżania.');
        return;
      }
      setSession({ id: data.id, started_at: data.started_at ?? now });
      setElapsedMinutes(0);
      try {
        await startWorkSession({
          orgId,
          locationId,
          userId: user.id,
          taskId: data.id,
          latitude: null,
          longitude: null,
        });
      } catch (sessionError) {
        console.warn('[SnowSessionSection] Nie udało się otworzyć sesji czasu pracy:', sessionError);
      }
      toast.success('Odśnieżanie rozpoczęte.');
      onSessionChange?.();
    } catch (err) {
      console.error('[SnowSessionSection] runStartWithGps:', err);
      toast.error('Wystąpił błąd.');
    } finally {
      setIsLoading(false);
      setIsStarting(false);
    }
  };

  const completeSnowSession = async (
    taskId: string,
    photoUrls: string[],
    startedAtIso?: string
  ) => {
    if (!orgId) return;
    try {
      // Zombie-session guard: cap completed_at to max 14h from started_at (consistent with PropertyDetailSheet)
      const MAX_SESSION_HOURS = 14;
      const maxSessionMs = MAX_SESSION_HOURS * 60 * 60 * 1000;
      let completedAtIso = new Date().toISOString();
      if (startedAtIso) {
        const startedMs = new Date(startedAtIso).getTime();
        const durationMs = Date.now() - startedMs;
        if (durationMs > maxSessionMs) {
          completedAtIso = new Date(startedMs + maxSessionMs).toISOString();
          console.warn('[SnowSessionSection] Sesja dłuższa niż 14h – completed_at z limitem', { taskId, startedAtIso, completedAtIso });
          toast.warning('Sesja trwała ponad 14h – czas zakończenia zapisany z limitem 14h.');
        }
      }

      // Snapshot: fetch worker snow rate and calculate total_cost (financial integrity)
      let appliedHourlyRate: number | null = null;
      let totalCost: number | null = null;
      const { data: taskRow } = await supabase
        .from('cleaning_tasks')
        .select('assigned_staff_id, started_at')
        .eq('id', taskId)
        .single();
      const staffId = taskRow?.assigned_staff_id;
      if (staffId) {
        const { data: profile } = await supabase
          .from('profiles')
          .select('snow_hourly_rate')
          .eq('id', staffId)
          .single();
        const rate = profile?.snow_hourly_rate ?? 0;
        if (rate > 0) {
          const startMs = (taskRow?.started_at ?? startedAtIso) ? new Date(taskRow?.started_at ?? startedAtIso!).getTime() : null;
          const endMs = new Date(completedAtIso).getTime();
          const durationHours = startMs != null ? (endMs - startMs) / (1000 * 60 * 60) : 0;
          const cappedHours = Math.min(durationHours, MAX_SESSION_HOURS);
          appliedHourlyRate = rate;
          totalCost = parseFloat((cappedHours * rate).toFixed(2));
        }
      }

      const updatePayload: Record<string, unknown> = {
        status: 'done',
        completed_at: completedAtIso,
      };
      if (appliedHourlyRate != null) updatePayload.applied_hourly_rate = appliedHourlyRate;
      if (totalCost != null) updatePayload.total_cost = totalCost;
      const { error: updateError } = await supabase
        .from('cleaning_tasks')
        .update(updatePayload)
        .eq('id', taskId)
        .eq('org_id', orgId);

      if (updateError) {
        console.error('[SnowSessionSection] Błąd zakończenia sesji:', updateError);
        toast.error('Nie udało się zakończyć odśnieżania.');
        return false;
      }

      if (photoUrls.length > 0) {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          for (const photoUrl of photoUrls) {
            await supabase.from('task_step_logs').insert({
              task_id: taskId,
              user_id: user.id,
              step_name: 'Odśnieżanie - zakończenie',
              step_id: 'snow_removal_end',
              completed: true,
              photo_url: photoUrl,
            } as Record<string, unknown>);
          }
        }
      }

      try {
        const { data: auth } = await supabase.auth.getUser();
        if (auth.user && orgId) {
          const openSessions = await fetchOpenWorkSessions(auth.user.id, orgId);
          const current = openSessions.find((session) => session.task_id === taskId || session.location_id === locationId);
          if (current) {
            await closeWorkSession({
              sessionId: current.id,
              locationId,
              reason: 'manual',
            });
          }
        }
      } catch (sessionError) {
        console.warn('[SnowSessionSection] Nie udało się zamknąć sesji czasu pracy:', sessionError);
      }

      setSession(null);
      setElapsedMinutes(0);
      setPendingStopTaskId(null);
      setShowPhotoModal(false);
      setPhotoFiles([]);
      setPreviewUrls((prev) => {
        prev.forEach((url) => URL.revokeObjectURL(url));
        return [];
      });
      toast.success('Odśnieżanie zakończone.');
      onSessionChange?.();
      return true;
    } catch (err) {
      console.error('[SnowSessionSection] completeSnowSession:', err);
      return false;
    }
  };

  const handleStopClick = () => {
    if (!session || isStopping || disabled) return;
    setPendingStopTaskId(session.id);
    setPhotoFiles([]);
    clearPreviews();
    setShowPhotoModal(true);
  };

  const handlePhotoModalSkip = async () => {
    if (!pendingStopTaskId) return;
    setIsStopping(true);
    try {
      await completeSnowSession(pendingStopTaskId, [], session?.started_at);
    } finally {
      setIsStopping(false);
    }
  };

  const handlePhotoModalConfirm = async () => {
    if (!pendingStopTaskId || !orgId) return;
    setIsUploadingPhoto(true);
    setIsStopping(true);
    try {
      const photoUrls: string[] = [];
      if (photoFiles.length > 0) {
        const sanitizedLocationId = locationId.replace(/[^a-zA-Z0-9-_]/g, '_');
        const basePath = `snow-jobs/${sanitizedLocationId}/${pendingStopTaskId}`;

        const uploadPromises = photoFiles.map((file, index) =>
          (async (): Promise<string | null> => {
            try {
              const compressed = await compressImage(file, { maxWidthOrHeight: 1280, initialQuality: 0.7 });
              const timestamp = Date.now();
              const randomString = Math.random().toString(36).slice(2, 10);
              const fileName = `${basePath}/${timestamp}_${randomString}.jpg`;
              const { error: uploadError } = await supabase.storage
                .from(BUCKET_NAME)
                .upload(fileName, compressed, { cacheControl: '3600', upsert: false });

              if (uploadError) {
                console.error('[SnowSessionSection] Błąd uploadu zdjęcia:', uploadError);
                return null;
              }
              const { data: urlData } = supabase.storage.from(BUCKET_NAME).getPublicUrl(fileName);
              return (urlData as { publicUrl?: string })?.publicUrl ?? null;
            } catch (err) {
              console.error('[SnowSessionSection] Błąd uploadu pojedynczego zdjęcia:', err);
              return null;
            }
          })()
        );

        const results = await Promise.all(uploadPromises);
        for (const url of results) {
          if (url) photoUrls.push(url);
        }
        if (photoUrls.length === 0 && photoFiles.length > 0) {
          toast.error('Nie udało się przesłać zdjęć. Zakończono sesję bez zdjęć.');
        } else if (photoUrls.length > 0 && photoUrls.length < photoFiles.length) {
          toast.warning(`Przesłano ${photoUrls.length} z ${photoFiles.length} zdjęć.`);
        }
      }
      await completeSnowSession(pendingStopTaskId, photoUrls, session?.started_at);
    } catch (err) {
      console.error('[SnowSessionSection] handlePhotoModalConfirm:', err);
      toast.error('Wystąpił błąd podczas zakończenia sesji.');
    } finally {
      setIsUploadingPhoto(false);
      setIsStopping(false);
    }
  };

  const handlePhotoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files?.length) return;
    const newFiles = Array.from(files).filter((f) => f.type.startsWith('image/'));
    if (newFiles.length > 0) {
      const newUrls = newFiles.map((f) => URL.createObjectURL(f));
      setPhotoFiles((prev) => [...prev, ...newFiles]);
      setPreviewUrls((prev) => [...prev, ...newUrls]);
    }
    e.target.value = '';
  };

  const removePhoto = (index: number) => {
    setPreviewUrls((prev) => {
      const url = prev[index];
      if (url) URL.revokeObjectURL(url);
      return prev.filter((_, i) => i !== index);
    });
    setPhotoFiles((prev) => prev.filter((_, i) => i !== index));
  };

  if (!locationId || !orgId) return null;

  return (
    <>
      <Card
        className={cn(
          'overflow-hidden border-2 transition-all',
          'bg-gradient-to-br from-sky-50 to-blue-50/80 dark:from-sky-950/40 dark:to-blue-950/30',
          'border-sky-200/60 dark:border-sky-800/50'
        )}
      >
        <CardHeader className="pb-2 pt-4 px-4">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-sky-500/15 dark:bg-sky-500/20">
              <Snowflake className="h-5 w-5 text-sky-600 dark:text-sky-400" />
            </div>
            <div>
              <h3 className="font-semibold text-sky-900 dark:text-sky-100">Prace Dodatkowe / Odśnieżanie</h3>
              <p className="text-xs text-sky-600/80 dark:text-sky-400/70">
                Sesja odśnieżania – Start / Stop z licznikiem czasu
              </p>
            </div>
          </div>
        </CardHeader>
        <CardContent className="px-4 pb-4 space-y-3">
          {!session ? (
            <div className="flex flex-col sm:flex-row gap-2">
              <Button
                className="bg-blue-600 hover:bg-blue-700 text-white"
                onClick={() => setShowStartConfirmDialog(true)}
                disabled={disabled || isLoading || isStarting}
              >
                {isStarting || isLoading ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Play className="h-4 w-4 mr-2" />
                )}
                Rozpocznij odśnieżanie
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center gap-2 flex-wrap">
                <span
                  className={cn(
                    'inline-flex h-2.5 w-2.5 rounded-full bg-amber-500',
                    'animate-pulse'
                  )}
                  aria-hidden
                />
                <Badge variant="secondary" className="bg-sky-100 text-sky-700 dark:bg-sky-900/60 dark:text-sky-300">
                  Sesja w toku
                </Badge>
                <span className="text-sm text-muted-foreground">
                  Czas: <strong>{elapsedMinutes}</strong> min
                </span>
              </div>
              <Button
                variant="destructive"
                className="w-full sm:w-auto"
                onClick={handleStopClick}
                disabled={disabled || isStopping}
              >
                {isStopping ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Square className="h-4 w-4 mr-2" />
                )}
                Zakończ odśnieżanie
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <AlertDialog open={showStartConfirmDialog} onOpenChange={setShowStartConfirmDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Potwierdzenie rozpoczęcia</AlertDialogTitle>
            <AlertDialogDescription>
              Czy na pewno chcesz rozpocząć rejestrację czasu odśnieżania?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isStarting}>Anuluj</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleStartConfirm}
              disabled={isStarting}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {isStarting ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : null}
              Tak, rozpocznij
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog
        open={showPhotoModal}
        onOpenChange={(open) => {
          if (isStopping || isUploadingPhoto) return;
          setShowPhotoModal(open);
          if (!open) {
            setPendingStopTaskId(null);
            setPhotoFiles([]);
            clearPreviews();
          }
        }}
      >
        <DialogContent
          className="sm:max-w-md"
          onPointerDownOutside={(e) => (isStopping || isUploadingPhoto) && e.preventDefault()}
          onEscapeKeyDown={(e) => (isStopping || isUploadingPhoto) && e.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle>Zdjęcia po zakończeniu odśnieżania</DialogTitle>
            <DialogDescription>
              Opcjonalnie dodaj zdjęcia jako dowód wykonanej pracy. Możesz wybrać wiele plików. Możesz pominąć ten krok.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={handlePhotoSelect}
            />
            {photoFiles.length > 0 ? (
              <div className="space-y-3">
                <div className="grid grid-cols-3 gap-2">
                  {photoFiles.map((file, idx) => (
                    <div
                      key={`${file.name}-${idx}`}
                      className="relative aspect-square rounded-lg overflow-hidden border border-border bg-muted group"
                    >
                      {previewUrls[idx] ? (
                        <img
                          src={previewUrls[idx]}
                          alt={file.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <ImagePlus className="h-8 w-8 text-muted-foreground" />
                        </div>
                      )}
                      <Button
                        type="button"
                        variant="destructive"
                        size="icon"
                        className="absolute top-1 right-1 h-6 w-6 rounded-full opacity-90 group-hover:opacity-100"
                        onClick={() => removePhoto(idx)}
                        disabled={isUploadingPhoto}
                        aria-label="Usuń zdjęcie"
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                  ))}
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="w-full"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isUploadingPhoto}
                >
                  <Camera className="h-4 w-4 mr-2" />
                  Dodaj kolejne
                </Button>
              </div>
            ) : (
              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploadingPhoto}
              >
                <Camera className="h-4 w-4 mr-2" />
                Dodaj zdjęcie
              </Button>
            )}
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="ghost" onClick={handlePhotoModalSkip} disabled={isUploadingPhoto || isStopping}>
              Pomiń
            </Button>
            <Button onClick={handlePhotoModalConfirm} disabled={isStopping || isUploadingPhoto}>
              {isUploadingPhoto ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Check className="h-4 w-4 mr-2" />
              )}
              {photoFiles.length > 0
                ? `Zapisz i zakończ (${photoFiles.length})`
                : 'Zakończ bez zdjęcia'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
